<template>
  <div class="contents-page" :class="{ 'contents-page--internal-generic': internalGenericMedia }">
    <PageContent class="contents-page__content">
      <section class="contents-panel">
        <div v-if="!internalGenericMedia" class="contents-summary">
          <section class="contents-summary__item">
            <h2 class="contents-summary__title">
              Brouillons <span class="contents-summary__hint">— contenu à programmer</span>
            </h2>
            <p class="contents-summary__counts">
              <span>{{ summaryCountLabel(draftCounts.articles, 'actualité', 'actualités') }}</span>
              <span class="contents-summary__separator" aria-hidden="true">·</span>
              <span>{{ summaryCountLabel(draftCounts.surveys, 'sondage', 'sondages') }}</span>
            </p>
          </section>
          <section class="contents-summary__item">
            <h2 class="contents-summary__title">
              Programmés <span class="contents-summary__hint">— contenus programmés dans les prochaines semaines</span>
            </h2>
            <p class="contents-summary__counts">
              <span>{{ summaryCountLabel(upcomingCounts.articles, 'actualité', 'actualités') }}</span>
              <span class="contents-summary__separator" aria-hidden="true">·</span>
              <span>{{ summaryCountLabel(upcomingCounts.surveys, 'sondage', 'sondages') }}</span>
              <span v-if="!hasUpcomingContent" class="contents-summary__empty">
                <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                Aucun contenu à venir sur {{ applicationLabel }}, c'est le moment de publier
              </span>
            </p>
          </section>
        </div>

        <header class="contents-toolbar">
          <label class="contents-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              v-model.trim="search"
              type="search"
              :placeholder="`Rechercher dans ${activeTabLabel.toLowerCase()}`"
              aria-label="Rechercher un contenu par titre"
            >
          </label>
          <div v-if="hasEditionRights" class="contents-toolbar__actions">
            <button type="button" class="contents-action contents-action--article" @click="createArticle">
              <i class="fas fa-plus-circle" aria-hidden="true"></i> Créer une actualité
            </button>
            <button type="button" class="contents-action contents-action--survey" @click="createSurvey">
              <i class="fas fa-plus-circle" aria-hidden="true"></i> Créer un sondage
            </button>
          </div>
          <nav class="contents-tabs" aria-label="Catégories de contenus">
            <button
              v-for="tab in visibleTabs"
              :key="tab.key"
              type="button"
              :class="{ 'contents-tabs__item--active': activeTab === tab.key }"
              class="contents-tabs__item"
              @click="selectTab(tab.key)"
            >
              {{ tab.label }}
            </button>
          </nav>
        </header>

        <div v-if="loading" class="contents-state">
          <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Chargement des contenus…
        </div>
        <div v-else-if="loadError" class="contents-state contents-state--error">
          <span>{{ loadError }}</span>
          <button type="button" @click="loadContents">Réessayer</button>
        </div>
        <div v-else class="contents-table-scroll">
          <table class="contents-table">
            <thead>
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.key"
                  :class="[
                    `contents-column--${column.key}`,
                    { 'contents-column--menu-open': openColumnMenu === column.key }
                  ]"
                  :aria-sort="columnAriaSort(column)"
                >
                  <div
                    class="contents-column-heading"
                    :class="{ 'contents-column-heading--interactive': isColumnInteractive(column) }"
                  >
                    <button
                      v-if="isColumnInteractive(column)"
                      type="button"
                      class="contents-column-trigger"
                      :id="`contents-column-trigger-${column.key}`"
                      :data-column-key="column.key"
                      :aria-expanded="openColumnMenu === column.key ? 'true' : 'false'"
                      :aria-controls="`contents-column-menu-${column.key}`"
                      aria-haspopup="dialog"
                      @click.stop="toggleColumnMenu(column)"
                    >
                      <span>{{ column.label }}</span>
                      <i :class="columnMenuIcon(column)" aria-hidden="true"></i>
                      <span v-if="hasColumnFilter(column)" class="contents-column-trigger__filter-dot">
                        <span class="sr-only">Filtre actif</span>
                      </span>
                    </button>
                    <span v-else>{{ column.label }}</span>

                    <div
                      v-if="openColumnMenu === column.key"
                      :id="`contents-column-menu-${column.key}`"
                      class="contents-column-popover"
                      role="dialog"
                      :aria-label="`Options de la colonne ${column.label}`"
                      @click.stop
                    >
                      <div v-if="column.sortable" class="contents-column-popover__group">
                        <span class="contents-column-popover__label">Trier</span>
                        <div class="contents-column-popover__actions">
                          <button
                            type="button"
                            :class="{ 'is-active': sort.key === column.key && sort.direction === 'asc' }"
                            @click="setSort(column.key, 'asc')"
                          >
                            {{ sortDirectionLabel(column, 'asc') }}
                          </button>
                          <button
                            type="button"
                            :class="{ 'is-active': sort.key === column.key && sort.direction === 'desc' }"
                            @click="setSort(column.key, 'desc')"
                          >
                            {{ sortDirectionLabel(column, 'desc') }}
                          </button>
                        </div>
                      </div>

                      <label v-if="column.filter === 'set'" class="contents-column-popover__group">
                        <span class="contents-column-popover__label">Filtrer</span>
                        <select
                          :value="filters[column.key] || ''"
                          :aria-label="`Filtrer par ${column.label.toLowerCase()}`"
                          @change="setColumnFilter(column.key, $event.target.value)"
                        >
                          <option value="">Tous</option>
                          <option v-for="value in filterValues(column.key)" :key="String(value)" :value="String(value)">
                            {{ filterLabel(column.key, value) }}
                          </option>
                        </select>
                      </label>

                      <div v-else-if="column.filter === 'date'" class="contents-column-popover__group">
                        <span class="contents-column-popover__label">Date ou période</span>
                        <label>
                          <span>Du</span>
                          <input v-model="dateFrom" type="date" aria-label="Date de début">
                        </label>
                        <label>
                          <span>Au</span>
                          <input v-model="dateTo" type="date" aria-label="Date de fin">
                        </label>
                      </div>

                      <button
                        v-if="hasColumnState(column)"
                        type="button"
                        class="contents-column-popover__reset"
                        @click="clearColumnState(column)"
                      >
                        Réinitialiser
                      </button>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in displayedItems"
                :key="`${item.type}-${item.uuid}`"
                :class="{ 'contents-row--editable': isRowEditable(item) }"
                :tabindex="isRowEditable(item) ? 0 : null"
                @click="editContent(item)"
                @keydown.enter.self="editContent(item)"
              >
                <td
                  v-for="column in columns"
                  :key="column.key"
                  :class="`contents-column--${column.key}`"
                  @click="handleCellClick($event, column)"
                >
                  <template v-if="column.key === 'preview'">
                    <button type="button" class="contents-icon-button" title="Aperçu" @click.stop="preview(item)">
                      <i class="far fa-eye" aria-hidden="true"></i>
                      <span class="sr-only">Aperçu de {{ item.title }}</span>
                    </button>
                  </template>
                  <template v-else-if="column.key === 'type'">
                    <span class="contents-type" :class="`contents-type--${item.type}`">{{ typeLabel(item.type) }}</span>
                  </template>
                  <template v-else-if="column.key === 'target'">{{ targetLabel(filterValue(item, 'target')) }}</template>
                  <template v-else-if="column.key === 'status'">
                    <span class="contents-status" :class="`contents-status--${item.status}`">{{ statusLabel(item.status) }}</span>
                  </template>
                  <template v-else-if="column.key === 'pin'">
                    <div v-if="item.type === 'article'" class="contents-pin-controls">
                      <input
                        type="checkbox"
                        :checked="toBoolean(item.is_pinned)"
                        :disabled="!hasEditionRights || pinningUuid === item.uuid"
                        :aria-label="toBoolean(item.is_pinned) ? 'Désépingler' : 'Épingler'"
                        @click.stop
                        @change.stop="togglePin(item, $event)"
                      >
                      <div v-if="toBoolean(item.is_pinned)" class="contents-pin-order">
                        <button type="button" title="Monter" :disabled="!canMovePin(item, -1)" @click.stop="movePin(item, -1)">
                          <i class="fas fa-chevron-up" aria-hidden="true"></i>
                        </button>
                        <button type="button" title="Descendre" :disabled="!canMovePin(item, 1)" @click.stop="movePin(item, 1)">
                          <i class="fas fa-chevron-down" aria-hidden="true"></i>
                        </button>
                      </div>
                    </div>
                    <span v-else>—</span>
                  </template>
                  <template v-else-if="column.key === 'date'">{{ dateDisplay(item) }}</template>
                  <template v-else-if="column.key === 'metric'">
                    <button
                      v-if="internalGenericMedia && Number(metricValue(item)) > 0"
                      type="button"
                      class="contents-duplication-count"
                      title="Voir les domaines ayant dupliqué ce contenu"
                      @click.stop="showDuplicationDomains(item)"
                    >
                      {{ displayMetric(item) }}
                    </button>
                    <span v-else>{{ displayMetric(item) }}</span>
                  </template>
                  <template v-else-if="column.key === 'title'">
                    <span class="contents-title" :title="item.title">{{ item.title || '—' }}</span>
                  </template>
                  <template v-else-if="column.key === 'duplicate'">
                    <button
                      type="button"
                      class="contents-duplicate"
                      :class="{ 'contents-duplicate--again': toBoolean(item.duplicated) }"
                      :disabled="duplicatingUuid === item.uuid || !hasEditionRights"
                      :title="toBoolean(item.duplicated) ? 'Vous avez déjà dupliqué ce contenu' : null"
                      @click.stop="duplicate(item)"
                    >
                      <i :class="toBoolean(item.duplicated) ? 'fas fa-check' : 'far fa-copy'" aria-hidden="true"></i>
                      {{ toBoolean(item.duplicated) ? 'Dupliquer à nouveau' : 'Dupliquer' }}
                    </button>
                  </template>
                  <template v-else-if="column.key === 'duplicateClient'">
                    <button
                      type="button"
                      class="contents-duplicate"
                      @click.stop="duplicateForClient(item)"
                    >
                      <i class="far fa-copy" aria-hidden="true"></i>
                      Dupliquer
                    </button>
                  </template>
                  <template v-else-if="column.key === 'followUp'">
                    <button
                      v-if="canFollowUp(item)"
                      type="button"
                      class="contents-follow-up"
                      :disabled="toBoolean(item.is_followed_up) || followUpPendingUuid === item.uuid"
                      :title="toBoolean(item.is_followed_up) ? 'Relance déjà effectuée' : 'Relancer l’administrateur'"
                      @click.stop="followUp(item)"
                    >
                      <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
                    </button>
                    <span v-else>—</span>
                  </template>
                  <template v-else-if="column.key === 'delete'">
                    <button
                      type="button"
                      class="contents-delete"
                      :disabled="[deletingUuid, disablingUuid].includes(item.uuid) || !hasEditionRights"
                      :title="activeTab === 'active' ? 'Retirer' : 'Supprimer'"
                      @click.stop="confirmDelete(item)"
                    >
                      <i class="far fa-trash-alt" aria-hidden="true"></i>
                      <span class="sr-only">{{ activeTab === 'active' ? 'Retirer' : 'Supprimer' }} {{ item.title }}</span>
                    </button>
                  </template>
                  <template v-else>{{ displayCellValue(item, column.key) }}</template>
                </td>
              </tr>
              <tr v-if="!displayedItems.length">
                <td :colspan="columns.length" class="contents-empty">Aucun contenu ne correspond à votre recherche ou aux filtres sélectionnés.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <footer v-if="!loading && !loadError" class="contents-footer">
          Affichage de {{ displayedItems.length }} contenu{{ displayedItems.length > 1 ? 's' : '' }}
        </footer>
      </section>
    </PageContent>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { format } from 'date-fns'
import { http } from '@/services/api.service'
import PageContent from '@/components/PageContent.vue'
import DashboardArticleModal from '@/components/dashboard/DashboardArticleModal.vue'
import DashboardNewsPreviewModal from '@/components/dashboard/DashboardNewsPreviewModal.vue'
import SurveyPreviewModal from '@/components/dashboard/SurveyPreviewModal.vue'
import OneGroup from '@/pages/Surveys/Modals/OneGroup.vue'
import ActiveContentRemovalModal from '@/pages/Contents/Modals/ActiveContentRemovalModal.vue'
import GenericContentDuplicationModal from '@/pages/Contents/Modals/GenericContentDuplicationModal.vue'
import GenericContentDuplicationsModal from '@/pages/Contents/Modals/GenericContentDuplicationsModal.vue'
import { INTERNAL_MEDIA_LABEL } from '@/utils/internalMediaLabel'
import { confirmMediaDuplication } from '@/utils/mediaDuplicationConfirm'

const TABS = [
  { key: 'media', label: INTERNAL_MEDIA_LABEL },
  { key: 'drafts', label: 'Brouillons' },
  { key: 'active', label: 'Activés' },
  { key: 'disabled', label: 'Désactivés' }
]

const COLUMNS = {
  media: [
    { key: 'preview', label: 'Aperçu' },
    { key: 'theme', label: 'Thème', filter: 'set', sortable: true },
    { key: 'type', label: 'Type', filter: 'set', sortable: true },
    { key: 'title', label: 'Titre', sortable: true },
    { key: 'date', label: 'Mise en ligne sur le média', filter: 'date', sortable: true },
    { key: 'duplicate', label: 'Dupliquer', filter: 'set', sortable: true }
  ],
  drafts: [
    { key: 'preview', label: 'Aperçu' },
    { key: 'type', label: 'Type', filter: 'set', sortable: true },
    { key: 'title', label: 'Titre', sortable: true },
    { key: 'date', label: 'Date', filter: 'date', sortable: true },
    { key: 'followUp', label: 'Relance CM' },
    { key: 'delete', label: 'Supprimer' }
  ],
  active: [
    { key: 'pin', label: 'Épingler' },
    { key: 'preview', label: 'Aperçu' },
    { key: 'type', label: 'Type', filter: 'set', sortable: true },
    { key: 'status', label: 'Statut', filter: 'set', sortable: true },
    { key: 'title', label: 'Titre', sortable: true },
    { key: 'date', label: 'Date', filter: 'date', sortable: true },
    { key: 'metric', label: 'Lectures / réponses', sortable: true },
    { key: 'delete', label: 'Supprimer' }
  ],
  disabled: [
    { key: 'preview', label: 'Aperçu' },
    { key: 'type', label: 'Type', filter: 'set', sortable: true },
    { key: 'title', label: 'Titre', sortable: true },
    { key: 'date', label: 'Date', filter: 'date', sortable: true },
    { key: 'metric', label: 'Lectures / réponses', sortable: true },
    { key: 'delete', label: 'Supprimer' }
  ]
}

export default {
  name: 'app-contents-list',
  components: { PageContent },
  props: {
    internalGenericMedia: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      activeTab: 'drafts',
      items: [],
      summary: {},
      meta: {},
      loading: false,
      loadRequestId: 0,
      loadError: '',
      search: '',
      filters: {},
      dateFrom: '',
      dateTo: '',
      sort: { key: null, direction: null },
      openColumnMenu: null,
      groups: null,
      pinningUuid: null,
      duplicatingUuid: null,
      deletingUuid: null,
      disablingUuid: null,
      exportingUuid: null,
      editorOpening: false,
      editorModalOpen: false,
      previewModalOpen: false,
      removalModalOpen: false,
      followUpPendingUuid: null
    }
  },
  computed: {
    ...mapGetters(['roles', 'domain', 'application']),
    applicationLabel () {
      return this.application?.label || 'Maviepro'
    },
    isMediaDomain () {
      return this.domain && this.domain.slug === '0-media-interne'
    },
    visibleTabs () {
      return TABS.filter(tab => !(tab.key === 'media' && this.isMediaDomain))
    },
    activeTabLabel () {
      const tab = TABS.find(entry => entry.key === this.activeTab)
      return tab ? tab.label : 'Contenus'
    },
    columns () {
      let columns = [...(COLUMNS[this.activeTab] || [])]

      if (this.isMediaDomain) {
        columns = columns
          .filter(column => ![
            'followUp',
            'pin',
            ...(this.internalGenericMedia ? ['status'] : [])
          ].includes(column.key))
          .map(column => column.key === 'metric'
            ? { ...column, label: 'Duplications' }
            : column)

        const typeIndex = columns.findIndex(column => column.key === 'type')
        columns.splice(typeIndex + 1, 0, { key: 'target', label: 'Cible', filter: 'set', sortable: true })

        // Duplication to clients is offered whatever the state of the source
        // (draft, scheduled, active, disabled): each copy gets its own status and date.
        if (this.internalGenericMedia) {
          const deleteIndex = columns.findIndex(column => column.key === 'delete')
          const duplicateColumn = { key: 'duplicateClient', label: 'Dupliquer chez un client' }
          if (deleteIndex === -1) columns.push(duplicateColumn)
          else columns.splice(deleteIndex, 0, duplicateColumn)
        }
      } else if (this.activeTab === 'drafts' && !this.canSeeFollowUp) {
        columns = columns.filter(column => column.key !== 'followUp')
      }

      return columns
    },
    hasEditionRights () {
      return this.roles.some(role => ['admin', 'director', 'community-manager'].includes(role))
    },
    canSeeFollowUp () {
      return this.roles.some(role => ['admin', 'community-manager'].includes(role))
    },
    draftCounts () {
      return this.summaryCounts(['drafts', 'draft', 'pending'])
    },
    upcomingCounts () {
      return this.summaryCounts(['upcoming', 'scheduled', 'future'])
    },
    hasUpcomingContent () {
      return this.upcomingCounts.articles + this.upcomingCounts.surveys > 0
    },
    filteredItems () {
      const search = this.search.toLocaleLowerCase('fr')
      return this.items.filter(item => {
        if (search && !String(item.title || '').toLocaleLowerCase('fr').includes(search)) return false
        if (!this.matchesSetFilters(item)) return false
        return this.matchesDateFilter(item)
      })
    },
    displayedItems () {
      const items = [...this.filteredItems]
      if (this.sort.key && this.sort.direction) {
        const factor = this.sort.direction === 'asc' ? 1 : -1
        items.sort((left, right) => {
          const a = this.sortValue(left, this.sort.key)
          const b = this.sortValue(right, this.sort.key)
          if (a === b) return 0
          if (a === null || a === undefined || a === '') return 1
          if (b === null || b === undefined || b === '') return -1
          if (typeof a === 'string' && typeof b === 'string') {
            return a.localeCompare(b, 'fr', { sensitivity: 'base' }) * factor
          }
          return a > b ? factor : -factor
        })
      }
      return this.pinnedFirst(items)
    }
  },
  watch: {
    domain () {
      if (this.isMediaDomain && this.activeTab === 'media') this.activeTab = 'drafts'
      this.groups = null
      this.openColumnMenu = null
      this.loadContents()
    }
  },
  mounted () {
    if (this.internalGenericMedia) this.sort = { key: 'date', direction: 'desc' }
    document.addEventListener('click', this.closeColumnMenu)
    document.addEventListener('keydown', this.handleColumnMenuKeydown)
    this.loadContents()
  },
  beforeDestroy () {
    document.removeEventListener('click', this.closeColumnMenu)
    document.removeEventListener('keydown', this.handleColumnMenuKeydown)
  },
  methods: {
    async loadContents () {
      const requestId = ++this.loadRequestId
      this.loading = true
      this.loadError = ''
      try {
        const { data } = await http.get('/contents', { params: { tab: this.activeTab } })
        if (requestId !== this.loadRequestId) return
        this.items = Array.isArray(data && data.items) ? data.items : []
        this.summary = (data && data.summary) || {}
        this.meta = (data && data.meta) || {}
      } catch (error) {
        if (requestId !== this.loadRequestId) return
        this.items = []
        this.loadError = 'Impossible de récupérer les contenus pour le moment.'
      } finally {
        if (requestId === this.loadRequestId) this.loading = false
      }
    },
    selectTab (tab) {
      if (tab === this.activeTab) return
      this.activeTab = tab
      this.search = ''
      this.filters = {}
      this.dateFrom = ''
      this.dateTo = ''
      this.sort = this.internalGenericMedia
        ? { key: 'date', direction: 'desc' }
        : { key: null, direction: null }
      this.openColumnMenu = null
      this.loadContents()
    },
    summaryCounts (keys) {
      let source = {}
      keys.some(key => {
        if (this.summary && this.summary[key] !== undefined) {
          source = this.summary[key]
          return true
        }
        return false
      })
      if (typeof source === 'number') return { articles: Number(source) || 0, surveys: 0 }
      return {
        articles: Number(source.articles !== undefined ? source.articles : (source.article || source.news || 0)) || 0,
        surveys: Number(source.surveys !== undefined ? source.surveys : (source.survey || source.polls || 0)) || 0
      }
    },
    summaryCountLabel (count, singular, plural) {
      const normalizedCount = Number(count) || 0
      return `${normalizedCount} ${normalizedCount > 1 ? plural : singular}`
    },
    isColumnInteractive (column) {
      return Boolean(column && (column.sortable || column.filter))
    },
    toggleColumnMenu (column) {
      if (!this.isColumnInteractive(column)) return
      this.openColumnMenu = this.openColumnMenu === column.key ? null : column.key
    },
    closeColumnMenu () {
      this.openColumnMenu = null
    },
    handleColumnMenuKeydown (event) {
      if (event.key !== 'Escape' || !this.openColumnMenu) return
      const columnKey = this.openColumnMenu
      this.closeColumnMenu()
      this.$nextTick(() => {
        const trigger = this.$el.querySelector(`[data-column-key="${columnKey}"]`)
        if (trigger) trigger.focus()
      })
    },
    setColumnFilter (key, value) {
      if (value) this.$set(this.filters, key, value)
      else this.$delete(this.filters, key)
    },
    setSort (key, direction) {
      this.sort = { key, direction }
    },
    hasColumnState (column) {
      if (!column) return false
      if (this.sort.key === column.key && this.sort.direction) return true
      return this.hasColumnFilter(column)
    },
    hasColumnFilter (column) {
      if (!column) return false
      if (column.filter === 'date') return Boolean(this.dateFrom || this.dateTo)
      return Boolean(this.filters[column.key])
    },
    clearColumnState (column) {
      if (!column) return
      if (this.sort.key === column.key) this.sort = { key: null, direction: null }
      if (column.filter === 'date') {
        this.dateFrom = ''
        this.dateTo = ''
      } else {
        this.$delete(this.filters, column.key)
      }
    },
    sortDirectionLabel (column, direction) {
      if (['title', 'theme', 'type', 'target', 'status'].includes(column.key)) {
        return direction === 'asc' ? 'A → Z' : 'Z → A'
      }
      if (column.key === 'date') {
        return direction === 'asc' ? 'Plus ancien' : 'Plus récent'
      }
      if (column.key === 'metric') {
        return direction === 'asc' ? 'Croissant' : 'Décroissant'
      }
      if (column.key === 'duplicate') {
        return direction === 'asc' ? 'Non dupliqué d’abord' : 'Dupliqué d’abord'
      }
      return direction === 'asc' ? 'Croissant' : 'Décroissant'
    },
    columnMenuIcon (column) {
      if (this.sort.key === column.key && this.sort.direction) {
        return this.sort.direction === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
      }
      if (this.hasColumnState(column)) return 'fas fa-filter'
      return 'fas fa-chevron-down'
    },
    columnAriaSort (column) {
      if (!column.sortable || this.sort.key !== column.key || !this.sort.direction) return null
      return this.sort.direction === 'asc' ? 'ascending' : 'descending'
    },
    filterValues (key) {
      const values = new Set()
      this.items.forEach(item => {
        const value = this.filterValue(item, key)
        if (value !== null && value !== undefined && value !== '') values.add(String(value))
      })
      return [...values].sort((a, b) => this.filterLabel(key, a).localeCompare(this.filterLabel(key, b), 'fr'))
    },
    filterValue (item, key) {
      if (key === 'duplicate') return this.toBoolean(item.duplicated) ? 'true' : 'false'
      if (key === 'target') return ['maviepro', 'tract'].includes(item.media_mvp_visibility) ? item.media_mvp_visibility : 'both'
      return this.normalizeCellValue(item[key])
    },
    normalizeCellValue (value) {
      if (value === null || value === undefined || value === '') return null
      if (typeof value === 'string' && value.trim().toLowerCase() === 'null') return null
      return value
    },
    displayCellValue (item, key) {
      const value = this.normalizeCellValue(item[key])
      return value === null ? '—' : value
    },
    filterLabel (key, value) {
      if (key === 'type') return this.typeLabel(value)
      if (key === 'status') return this.statusLabel(value)
      if (key === 'duplicate') return String(value) === 'true' ? 'Dupliqué' : 'Non dupliqué'
      if (key === 'target') return this.targetLabel(value)
      return value
    },
    matchesSetFilters (item) {
      return Object.keys(this.filters).every(key => {
        const expected = this.filters[key]
        return !expected || String(this.filterValue(item, key)) === String(expected)
      })
    },
    matchesDateFilter (item) {
      if (!this.dateFrom && !this.dateTo) return true
      if (this.activeTab === 'disabled') {
        const startValue = item.type === 'survey' ? item.start_at : (item.publication_date || item.date)
        const endValue = item.type === 'survey' ? item.end_at : item.disabled_at
        const start = this.dateDay(startValue) || this.dateDay(endValue)
        const end = this.dateDay(endValue) || start
        if (!start || !end) return false
        return (!this.dateTo || start <= this.dateTo) && (!this.dateFrom || end >= this.dateFrom)
      }
      const date = this.itemDate(item)
      if (!date) return false
      const day = format(date, 'yyyy-MM-dd')
      return (!this.dateFrom || day >= this.dateFrom) && (!this.dateTo || day <= this.dateTo)
    },
    dateDay (value) {
      if (!value || value === '—') return null
      const date = new Date(String(value).replace(' ', 'T'))
      return Number.isNaN(date.getTime()) ? null : format(date, 'yyyy-MM-dd')
    },
    itemDate (item) {
      const value = item.date || item.publication_date || item.start_at
      if (!value) return null
      const date = new Date(String(value).replace(' ', 'T'))
      return Number.isNaN(date.getTime()) ? null : date
    },
    sortValue (item, key) {
      if (key === 'date') {
        const date = this.itemDate(item)
        return date ? date.getTime() : null
      }
      if (key === 'metric') {
        const metric = this.metricValue(item)
        return metric === null ? null : Number(metric)
      }
      if (key === 'duplicate') return this.toBoolean(item.duplicated) ? 1 : 0
      const value = this.filterValue(item, key)
      return typeof value === 'string' ? value.toLocaleLowerCase('fr') : value
    },
    metricValue (item) {
      const value = this.isMediaDomain ? item.duplications_count : item.metric
      return value === null || value === undefined ? null : value
    },
    displayMetric (item) {
      const value = this.metricValue(item)
      return value === null ? '—' : value
    },
    pinnedFirst (items) {
      if (this.activeTab !== 'active' || this.isMediaDomain) return items
      return items.sort((left, right) => {
        const leftPinned = left.type === 'article' && this.toBoolean(left.is_pinned)
        const rightPinned = right.type === 'article' && this.toBoolean(right.is_pinned)
        if (leftPinned !== rightPinned) return leftPinned ? -1 : 1
        if (leftPinned && rightPinned) return Number(left.pinned_position || 0) - Number(right.pinned_position || 0)
        return 0
      })
    },
    typeLabel (type) {
      return type === 'survey' ? 'Sondage' : 'Actualité'
    },
    targetLabel (target) {
      return { maviepro: 'Maviepro', tract: 'Tract' }[target] || 'Maviepro & Tract'
    },
    statusLabel (status) {
      return { scheduled: 'Programmé', published: 'Publié', notified: 'Publié' }[status] || status || '—'
    },
    toBoolean (value) {
      return [true, 1, '1', 'true'].includes(value)
    },
    formatDate (value, includeTime = true) {
      if (!value) return '—'
      const date = new Date(String(value).replace(' ', 'T'))
      if (Number.isNaN(date.getTime())) return String(value)
      return format(date, includeTime ? 'dd/MM/yyyy - HH:mm' : 'dd/MM/yyyy')
    },
    formatDateRange (value, includeTime = true) {
      const bounds = String(value || '').split(/\s*(?:→|->)\s*/)
      if (bounds.length !== 2) return this.formatDate(value, includeTime)
      return `${this.formatDate(bounds[0], includeTime)} → ${this.formatDate(bounds[1], includeTime)}`
    },
    dateDisplay (item) {
      if (this.activeTab === 'disabled') {
        if (item.date_range) return this.formatDateRange(item.date_range, false)
        const start = item.type === 'survey' ? item.start_at : (item.publication_date || item.date)
        const end = item.type === 'survey' ? item.end_at : item.disabled_at
        return `${this.formatDate(start, false)} → ${this.formatDate(end, false)}`
      }
      const includeTime = ['active', 'drafts'].includes(this.activeTab)
      return this.formatDate(item.date || item.publication_date || item.start_at, includeTime)
    },
    isActionColumn (column) {
      return Boolean(column && ['preview', 'pin', 'duplicate', 'duplicateClient', 'followUp', 'delete'].includes(column.key))
    },
    handleCellClick (event, column) {
      if (this.isActionColumn(column)) event.stopPropagation()
    },
    isRowEditable (item) {
      return Boolean(item && this.hasEditionRights && this.activeTab !== 'media')
    },
    preview (item) {
      if (this.previewModalOpen) return
      const isMedia = this.activeTab === 'media'
      const component = item.type === 'survey' ? SurveyPreviewModal : DashboardNewsPreviewModal
      const props = item.type === 'survey'
        ? {
          surveyUuid: item.uuid,
          surveyGroupUuid: item.is_single ? null : item.uuid,
          initialTitle: item.title,
          source: isMedia ? 'maviepro' : 'pending',
          canExportStats: this.canExportSurveyStats(item),
          canViewResults: this.canExportSurveyStats(item),
          exportStats: () => this.downloadSurveyStats(item)
        }
        : {
          articleUuid: item.uuid,
          initialTitle: item.title,
          source: isMedia ? 'maviepro' : 'pending'
        }
      const modal = this.$buefy.modal.open({
        parent: this,
        component,
        hasModalCard: false,
        width: 900,
        customClass: 'dashboard-modal content-editor-modal-host',
        canCancel: ['escape', 'outside'],
        props,
        events: { close: () => modal.close() }
      })
      this.previewModalOpen = true
      modal.$once('close', () => {
        this.previewModalOpen = false
      })
    },
    canExportSurveyStats (item) {
      return Boolean(
        item &&
        item.type === 'survey' &&
        this.hasEditionRights &&
        ['active', 'disabled'].includes(this.activeTab) &&
        !(this.isMediaDomain && this.internalGenericMedia)
      )
    },
    async ensureGroups () {
      if (this.groups) return this.groups
      const { data } = await http.get('/groups')
      this.groups = Array.isArray(data) ? data : []
      return this.groups
    },
    async openArticleModal (article, options = {}) {
      if (this.editorModalOpen) return null
      const freshWhenInactive = Boolean(options.freshWhenInactive || !(article && article.uuid))
      const groups = await this.ensureGroups()
      const modal = this.$buefy.modal.open({
        parent: this,
        component: DashboardArticleModal,
        hasModalCard: false,
        width: 960,
        destroyOnHide: false,
        customClass: 'dashboard-modal dashboard-modal--article content-editor-modal-host',
        canCancel: ['escape', 'outside'],
        props: {
          article: article || null,
          articleKey: article && article.uuid ? article.uuid : null,
          groups,
          stayOnDashboard: true,
          canRenotify: this.activeTab === 'active'
        },
        events: {
          close: () => modal.close(),
          refresh: (savedArticle, nextStatus, metadata = {}) => {
            modal.close()
            if (metadata.action === 'duplicate') {
              this.handleContentSaved(savedArticle, nextStatus, metadata, true)
              return
            }
            if (metadata.action === 'save') {
              this.handleContentSaved(savedArticle, nextStatus, metadata, freshWhenInactive)
              return
            }
            this.loadContents()
          }
        }
      })
      this.trackEditorModal(modal)
      return modal
    },
    trackEditorModal (modal) {
      this.editorModalOpen = true
      modal.$once('close', () => {
        this.editorModalOpen = false
      })
    },
    async createArticle () {
      if (this.editorOpening || this.editorModalOpen) return
      this.editorOpening = true
      try {
        await this.openArticleModal(null)
      } catch (error) {
        this.notifyError('Impossible d’ouvrir la création d’actualité.')
      } finally {
        this.editorOpening = false
      }
    },
    createSurvey () {
      if (this.editorOpening || this.editorModalOpen) return
      this.editorOpening = true
      try {
        this.openSurveyModal(null)
      } finally {
        this.editorOpening = false
      }
    },
    openSurveyModal (editGroup, options = {}) {
      if (this.editorModalOpen) return null
      const freshWhenInactive = Boolean(options.freshWhenInactive || !editGroup)
      const modal = this.$buefy.modal.open({
        parent: this,
        component: OneGroup,
        hasModalCard: true,
        width: 960,
        destroyOnHide: false,
        customClass: 'content-editor-modal-host',
        canCancel: ['escape', 'outside'],
        props: {
          editGroup,
          canRenotify: this.activeTab === 'active'
        },
        events: {
          created: (payload, metadata = {}) => {
            modal.close()
            this.handleContentSaved(payload, null, metadata, true)
          },
          updated: (payload, metadata = {}) => {
            modal.close()
            if (metadata.action === 'duplicate') {
              this.handleContentSaved(payload, null, metadata, true)
              return
            }
            if (metadata.action === 'save') {
              this.handleContentSaved(payload, null, metadata, freshWhenInactive)
              return
            }
            this.loadContents()
          },
          deleted: () => {
            modal.close()
            this.loadContents()
          }
        }
      })
      this.trackEditorModal(modal)
      return modal
    },
    savedContent (payload) {
      return payload && payload.group ? payload.group : (payload || {})
    },
    savedContentTab (payload, nextStatus, metadata, freshWhenInactive) {
      const content = this.savedContent(payload)
      const hasSavedEnabledState = Object.prototype.hasOwnProperty.call(content, 'is_enabled')
      const isEnabled = hasSavedEnabledState
        ? this.toBoolean(content.is_enabled)
        : (metadata.isEnabled !== undefined
          ? this.toBoolean(metadata.isEnabled)
          : nextStatus === 'active')

      if (isEnabled) {
        const endAt = content.end_at
          ? new Date(String(content.end_at).replace(' ', 'T'))
          : null
        const hasEnded = endAt && !Number.isNaN(endAt.getTime()) && endAt.getTime() < Date.now()
        return hasEnded ? 'disabled' : 'active'
      }
      if (freshWhenInactive) return 'drafts'

      const wasActivated = content.first_activation_date || content.first_activated_at
      return wasActivated ? 'disabled' : 'drafts'
    },
    handleContentSaved (payload, nextStatus, metadata = {}, freshWhenInactive = false) {
      const targetTab = this.savedContentTab(payload, nextStatus, metadata, freshWhenInactive)
      if (targetTab === this.activeTab) {
        this.loadContents()
        return
      }
      this.selectTab(targetTab)
    },
    async resolveSurveyGroup (uuid) {
      if (this.internalGenericMedia) {
        const { data } = await http.get(`/internal/generic-contents/media/surveys/${uuid}/edit`)
        if (!data || String(data.uuid) !== String(uuid)) throw new Error('Survey group not found')
        return data
      }

      const { data } = await http.get('/domain/surveys', {
        params: { multiple: true, past: true, uuid }
      })
      const group = Array.isArray(data) ? data.find(entry => String(entry.uuid) === String(uuid)) : null
      if (!group) throw new Error('Survey group not found')
      return group
    },
    async editContent (item) {
      if (!this.isRowEditable(item) || this.editorOpening || this.editorModalOpen) return
      this.editorOpening = true
      try {
        if (item.type === 'article') {
          const { data } = await http.get(`/article/${item.uuid}`)
          await this.openArticleModal(data)
          return
        }
        const group = await this.resolveSurveyGroup(item.uuid)
        this.openSurveyModal(group)
      } catch (error) {
        this.notifyError(item.type === 'article'
          ? 'Impossible d’ouvrir cette actualité.'
          : 'Impossible d’ouvrir ce sondage.')
      } finally {
        this.editorOpening = false
      }
    },
    duplicate (item) {
      if (this.duplicatingUuid) return
      const contentLabel = item.type === 'survey' ? 'ce sondage' : 'cette actualité'
      confirmMediaDuplication(this, {
        alreadyDuplicated: this.toBoolean(item.duplicated),
        localCopy: item.local_copy || null,
        contentLabel,
        title: 'Duplication',
        message: `Voulez-vous vraiment dupliquer ${contentLabel} ?`,
        onConfirm: async (payload) => {
          this.duplicatingUuid = item.uuid
          try {
            const url = item.type === 'survey' ? `/surveys/${item.uuid}/duplicate` : `/article/${item.uuid}/duplicate`
            const { data } = await http.post(url, payload)
            item.duplicated = true
            this.$buefy.notification.open({
              duration: 3000,
              message: 'Contenu dupliqué avec succès.',
              position: 'is-bottom-right',
              type: 'is-success'
            })
            await this.loadContents()

            try {
              if (item.type === 'article') {
                await this.openArticleModal(data, { freshWhenInactive: true })
              } else {
                const duplicatedUuid = data && data.group ? data.group.uuid : (data && data.uuid)
                if (!duplicatedUuid) throw new Error('Duplicated survey UUID missing')
                const duplicatedGroup = await this.resolveSurveyGroup(duplicatedUuid)
                this.openSurveyModal(duplicatedGroup, { freshWhenInactive: true })
              }
            } catch (editorError) {
              this.$buefy.notification.open({
                duration: 4500,
                message: 'Le contenu a bien été dupliqué, mais sa fenêtre de modification n’a pas pu s’ouvrir.',
                position: 'is-bottom-left',
                type: 'is-warning'
              })
            }
          } catch (error) {
            this.notifyError('Impossible de dupliquer ce contenu.')
          } finally {
            this.duplicatingUuid = null
          }
        }
      })
    },
    duplicateForClient (item) {
      const modal = this.$buefy.modal.open({
        parent: this,
        component: GenericContentDuplicationModal,
        hasModalCard: true,
        width: 1080,
        canCancel: ['escape', 'outside'],
        props: { content: item },
        events: {
          close: () => modal.close(),
          duplicated: () => this.loadContents()
        }
      })
    },
    showDuplicationDomains (item) {
      const modal = this.$buefy.modal.open({
        parent: this,
        component: GenericContentDuplicationsModal,
        hasModalCard: true,
        width: 860,
        canCancel: ['escape', 'outside'],
        props: { content: item },
        events: { close: () => modal.close() }
      })
    },
    async togglePin (item, event) {
      const nextPinned = !this.toBoolean(item.is_pinned)
      const pinnedItems = this.items.filter(entry => entry.type === 'article' && this.toBoolean(entry.is_pinned))
      if (nextPinned && pinnedItems.length >= 6) {
        if (event && event.target) event.target.checked = this.toBoolean(item.is_pinned)
        this.notifyError('Vous ne pouvez pas épingler plus de 6 actualités.')
        return
      }
      this.pinningUuid = item.uuid
      try {
        const positions = pinnedItems.map(entry => Number(entry.pinned_position === null ? -1 : entry.pinned_position))
        const pinnedPosition = nextPinned ? Math.max(-1, ...positions) + 1 : null
        await http.post(`/article/${item.uuid}`, {
          is_pinned: nextPinned ? 1 : 0,
          pinned_position: pinnedPosition
        })
        item.is_pinned = nextPinned
        item.pinned_position = pinnedPosition
        if (!nextPinned) await this.compactPinnedPositions()
      } catch (error) {
        if (event && event.target) event.target.checked = this.toBoolean(item.is_pinned)
        this.notifyError('Impossible de modifier l’épinglage.')
      } finally {
        this.pinningUuid = null
      }
    },
    async compactPinnedPositions () {
      const pinned = this.items
        .filter(item => item.type === 'article' && this.toBoolean(item.is_pinned))
        .sort((a, b) => Number(a.pinned_position || 0) - Number(b.pinned_position || 0))
      await Promise.all(pinned.map((item, index) => {
        item.pinned_position = index
        return http.post(`/article/${item.uuid}`, { pinned_position: index })
      }))
    },
    pinnedItems () {
      return this.items
        .filter(item => item.type === 'article' && this.toBoolean(item.is_pinned))
        .sort((a, b) => Number(a.pinned_position || 0) - Number(b.pinned_position || 0))
    },
    canMovePin (item, direction) {
      if (!this.hasEditionRights || this.pinningUuid) return false
      const index = this.pinnedItems().findIndex(entry => entry.uuid === item.uuid)
      return index >= 0 && index + direction >= 0 && index + direction < this.pinnedItems().length
    },
    async movePin (item, direction) {
      const pinned = this.pinnedItems()
      const index = pinned.findIndex(entry => entry.uuid === item.uuid)
      const target = pinned[index + direction]
      if (!target) return
      this.pinningUuid = item.uuid
      try {
        const currentPosition = Number(item.pinned_position || 0)
        const targetPosition = Number(target.pinned_position || 0)
        await Promise.all([
          http.post(`/article/${item.uuid}`, { pinned_position: targetPosition }),
          http.post(`/article/${target.uuid}`, { pinned_position: currentPosition })
        ])
        item.pinned_position = targetPosition
        target.pinned_position = currentPosition
      } catch (error) {
        this.notifyError('Impossible de modifier l’ordre des contenus épinglés.')
      } finally {
        this.pinningUuid = null
      }
    },
    canFollowUp (item) {
      return item.type === 'article' || (item.type === 'survey' && !item.is_single)
    },
    followUp (item) {
      if (this.toBoolean(item.is_followed_up) || this.followUpPendingUuid) return
      this.followUpPendingUuid = item.uuid
      const url = item.type === 'article'
        ? `/article/${item.uuid}/followup`
        : `/surveys/${item.uuid}/followup-group`
      http.post(url)
        .then(() => {
          item.is_followed_up = true
          this.$notify({ group: 'maviepro-success', text: 'Rappel envoyé' })
        })
        .catch(() => this.$notify({ group: 'maviepro-error', text: 'Problème lors de l’envoi du rappel' }))
        .finally(() => {
          this.followUpPendingUuid = null
        })
    },
    confirmDelete (item) {
      if (this.activeTab === 'active') {
        this.openActiveContentRemoval(item)
        return
      }
      this.confirmPermanentDelete(item)
    },
    openActiveContentRemoval (item) {
      if (this.removalModalOpen) return
      const modal = this.$buefy.modal.open({
        parent: this,
        component: ActiveContentRemovalModal,
        hasModalCard: true,
        width: 600,
        props: {
          title: item.title || '',
          contentType: item.type
        },
        events: {
          close: () => modal.close(),
          disable: () => {
            modal.close()
            this.disableContent(item)
          },
          delete: () => {
            modal.close()
            this.confirmPermanentDelete(item)
          }
        }
      })
      this.removalModalOpen = true
      modal.$once('close', () => {
        this.removalModalOpen = false
      })
    },
    confirmPermanentDelete (item) {
      this.$buefy.dialog.confirm({
        title: 'Suppression définitive',
        message: `Êtes-vous sûr de vouloir supprimer définitivement « ${item.title} » ? Cette action est irréversible.`,
        cancelText: 'Annuler',
        confirmText: 'Supprimer définitivement',
        type: 'is-danger',
        onConfirm: () => this.deleteContent(item)
      })
    },
    async disableContent (item) {
      this.disablingUuid = item.uuid
      try {
        await http.post(`/contents/${item.type}/${item.uuid}/disable`)
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Le contenu a bien été désactivé.',
          position: 'is-bottom-right',
          type: 'is-success'
        })
        this.selectTab('disabled')
      } catch (error) {
        this.notifyError('Impossible de désactiver ce contenu.')
      } finally {
        this.disablingUuid = null
      }
    },
    async downloadSurveyStats (item) {
      if (!item || item.type !== 'survey' || this.exportingUuid) return

      this.exportingUuid = item.uuid
      try {
        const endpoint = item.is_single
          ? `/surveys/${item.uuid}/stats`
          : `/surveys/${item.uuid}/groupstats`
        const response = await http.get(endpoint, { responseType: 'blob' })
        const blob = response.data instanceof Blob ? response.data : new Blob([response.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `stats_${item.uuid}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => window.URL.revokeObjectURL(url), 100)
      } catch (error) {
        this.notifyError('Impossible d’exporter les statistiques de ce sondage.')
      } finally {
        this.exportingUuid = null
      }
    },
    async deleteContent (item) {
      this.deletingUuid = item.uuid
      try {
        const url = item.type === 'article'
          ? `/article/${item.uuid}`
          : (item.is_single ? `/surveys/${item.uuid}` : `/surveys/deletegroup/${item.uuid}`)
        await http.delete(url)
        this.items = this.items.filter(entry => !(entry.uuid === item.uuid && entry.type === item.type))
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Le contenu a bien été supprimé.',
          position: 'is-bottom-right',
          type: 'is-success'
        })
        await this.loadContents()
      } catch (error) {
        this.notifyError('Impossible de supprimer ce contenu.')
      } finally {
        this.deletingUuid = null
      }
    },
    notifyError (message) {
      this.$buefy.notification.open({
        duration: 3500,
        message,
        position: 'is-bottom-left',
        type: 'is-warning',
        hasIcon: true,
        icon: 'server'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.contents-page { background: #f3f4f6; height: calc(100vh - 64px); min-width: 0; overflow: hidden; width: 100%; }
.contents-page--internal-generic { height: 100%; min-height: 0; }
.contents-page__content { display: flex; flex-direction: column; height: calc(100% - 40px); min-height: 0; }
.contents-panel { background: #fff; border: 1px solid #bfdbfe; border-radius: 14px; box-shadow: 0 4px 12px rgba(15, 23, 42, .06); }
.contents-summary { border-bottom: 1px solid #e5e7eb; display: grid; flex: 0 0 auto; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.contents-summary__item { min-width: 0; padding: 16px 28px; position: relative; }
.contents-summary__item + .contents-summary__item::before { background: #e5e7eb; bottom: 14px; content: ''; left: 0; position: absolute; top: 14px; width: 1px; }
.contents-summary__title { color: #2563eb; font-size: 14px; font-weight: 600; line-height: 20px; margin: 0; }
.contents-summary__hint { color: #9ca3af; font-size: 12px; font-style: italic; font-weight: 400; margin-left: 4px; }
.contents-summary__counts { align-items: center; color: #111827; display: flex; flex-wrap: wrap; font-size: 14px; gap: 0 6px; line-height: 20px; margin: 6px 0 0; }
.contents-summary__separator { color: #9ca3af; }
.contents-summary__empty { color: #dc2626; margin-left: 10px; }
.contents-summary__empty i { margin-right: 6px; }
.contents-toolbar__actions { display: flex; gap: 12px; }
.contents-action { border: 0; border-radius: 10px; color: #fff; font-size: 14px; font-weight: 400; min-width: 210px; padding: 14px 18px; }
.contents-action i { margin-right: 8px; }
.contents-action--article { background: #2563eb; }
.contents-action--article:hover { background: #1d4ed8; }
.contents-action--survey { background: #f97316; }
.contents-action--survey:hover { background: #ea580c; }
.contents-panel { display: flex; flex: 1 1 auto; flex-direction: column; max-width: 100%; min-height: 0; min-width: 0; overflow: hidden; }
.contents-toolbar { align-items: center; display: flex; flex: 0 0 auto; flex-wrap: wrap; gap: 18px 24px; padding: 22px 28px; }
.contents-search { align-items: center; border: 1px solid #d1d5db; border-radius: 9px; display: flex; flex: 1 1 260px; max-width: 360px; min-width: 240px; padding: 0 14px; }
.contents-search:focus-within { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, .12); }
.contents-search i { color: #9ca3af; }
.contents-search input { border: 0; box-shadow: none; font-size: 14px; min-width: 0; outline: none; padding: 11px; width: 100%; }
.contents-tabs { background: #f3f4f6; border-radius: 999px; display: flex; margin-left: auto; padding: 4px; }
.contents-tabs__item { background: transparent; border: 0; border-radius: 999px; color: #4b5563; font-size: 14px; font-weight: 400; padding: 10px 22px; white-space: nowrap; }
.contents-tabs__item--active { background: #2563eb; box-shadow: 0 2px 5px rgba(37, 99, 235, .25); color: #fff; }
.contents-table-scroll { contain: paint; flex: 1 1 auto; max-width: 100%; min-height: 0; min-width: 0; overflow: auto; width: 100%; }
.contents-table {
  border-collapse: separate; border-spacing: 0; min-width: 1200px; table-layout: fixed; width: 100%;
  th { background: #f9fafb; color: #4b5563; font-size: 14px; font-weight: 700; letter-spacing: .03em; padding: 12px 14px; position: sticky; text-align: left; text-transform: uppercase; top: 0; vertical-align: top; white-space: nowrap; z-index: 2; }
  td { border-top: 1px solid #e5e7eb; box-sizing: border-box; color: #111827; height: 52px; line-height: 1.25rem; padding: 6px 14px; vertical-align: middle; }
  tbody tr { height: 52px; }
  tbody tr:hover { background: #f9fafb; }
}
.contents-table th.contents-column--menu-open { z-index: 5; }
.contents-table tbody tr.contents-row--editable { cursor: pointer; }
.contents-table tbody tr.contents-row--editable:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: -2px;
}
.contents-column-heading { align-items: center; display: flex; min-height: 24px; position: relative; }
.contents-column-trigger {
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  gap: 7px;
  letter-spacing: inherit;
  padding: 3px 0;
  text-align: left;
  text-transform: inherit;
}
.contents-column-trigger:hover,
.contents-column-trigger:focus-visible { color: #2563eb; }
.contents-column-trigger:focus-visible { outline: 2px solid #2563eb; outline-offset: 3px; }
.contents-column-trigger__filter-dot {
  background: #2563eb;
  border: 2px solid #f9fafb;
  border-radius: 999px;
  height: 8px;
  margin-left: -4px;
  width: 8px;
}
.contents-column-popover {
  background: #fff;
  border: 1px solid #dbe1ea;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, .18);
  color: #111827;
  font-size: 12px;
  font-weight: 400;
  left: 0;
  letter-spacing: normal;
  min-width: 230px;
  padding: 12px;
  position: absolute;
  text-transform: none;
  top: calc(100% + 8px);
  z-index: 10;
}
.contents-column--metric .contents-column-popover,
.contents-column--duplicate .contents-column-popover,
.contents-column--date:last-child .contents-column-popover { left: auto; right: 0; }
.contents-column-popover__group {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin: 0 0 12px;
}
.contents-column-popover__group:last-child { margin-bottom: 0; }
.contents-column-popover__label { color: #64748b; font-size: 11px; font-weight: 700; }
.contents-column-popover__actions { display: grid; gap: 6px; grid-template-columns: 1fr 1fr; }
.contents-column-popover button,
.contents-column-popover select,
.contents-column-popover input {
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #111827;
  font: inherit;
  min-height: 34px;
  padding: 7px 9px;
  text-transform: none;
}
.contents-column-popover select,
.contents-column-popover input { width: 100%; }
.contents-column-popover__actions button { cursor: pointer; }
.contents-column-popover__actions button:hover,
.contents-column-popover__actions button.is-active {
  background: #eff6ff;
  border-color: #2563eb;
  color: #1d4ed8;
}
.contents-column-popover__group label {
  align-items: center;
  display: grid;
  gap: 7px;
  grid-template-columns: 24px 1fr;
}
.contents-column-popover__reset {
  color: #2563eb !important;
  cursor: pointer;
  width: 100%;
}
.contents-column-popover__reset:hover { background: #eff6ff; border-color: #93c5fd; }
.contents-column--preview, .contents-column--pin, .contents-column--delete { width: 85px; }
.contents-column--followUp { width: 135px; }
.contents-column--type { width: 130px; }
.contents-column--target { width: 170px; }
.contents-column--theme { width: 150px; }
.contents-column--status { width: 145px; }
.contents-column--date { width: 260px; }
.contents-column--metric { width: 225px; }
.contents-column--duplicate { width: 150px; }
.contents-column--duplicateClient { width: 190px; }
.contents-table th.contents-column--preview,
.contents-table th.contents-column--delete {
  text-align: center;
}
.contents-table th.contents-column--preview .contents-column-heading,
.contents-table th.contents-column--delete .contents-column-heading {
  justify-content: center;
}
.contents-table td.contents-column--preview,
.contents-table td.contents-column--delete {
  padding: 0;
  text-align: center;
}
.contents-page--internal-generic .contents-table td.contents-column--metric { text-align: center; }
.contents-title {
  display: -webkit-box;
  font-size: 14px;
  max-height: 2.5rem;
  overflow: hidden;
  line-height: 1.25rem;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.contents-icon-button, .contents-follow-up, .contents-delete { background: transparent; border: 0; font-size: 17px; }
.contents-table td.contents-column--preview .contents-icon-button,
.contents-table td.contents-column--delete .contents-delete {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 52px;
  width: 100%;
}
.contents-icon-button { color: #9ca3af; }
.contents-icon-button:hover { color: #2563eb; }
.contents-pin-controls { align-items: center; display: flex; gap: 5px; }
.contents-pin-controls input { accent-color: #2563eb; height: 16px; width: 16px; }
.contents-pin-order { display: flex; flex-direction: column; }
.contents-pin-order button { background: transparent; border: 0; color: #2563eb; font-size: 8px; line-height: 10px; padding: 0 2px; }
.contents-pin-order button:disabled { color: #d1d5db; }
.contents-follow-up { color: #2563eb; }
.contents-follow-up:disabled { color: #22c55e; }
.contents-delete { color: #ef4444; }
.contents-type { border-radius: 999px; display: inline-flex; font-size: 14px; font-weight: 600; padding: 5px 10px; }
.contents-type--article { background: #dbeafe; color: #1d4ed8; }
.contents-type--survey { background: #ffedd5; color: #c2410c; }
// Le statut se lit dans son libellé : pas de pastille colorée.
.contents-status { font-size: 14px; }
.contents-duplicate {
  background: #fff; border: 1px solid #2563eb; border-radius: 7px; color: #2563eb; font-size: 13px; font-weight: 600; padding: 8px 16px;
  display: inline-flex; align-items: center; justify-content: center; width: 100%; min-width: 168px; white-space: nowrap;
  i { margin-right: 5px; }
  &:disabled { background: #f3f4f6; border-color: #d1d5db; color: #9ca3af; }
  &--again { background: #eff6ff; border-color: #2563eb; color: #2563eb; }
}
.contents-duplication-count {
  background: #eff6ff;
  border: 0;
  border-radius: 999px;
  color: #1d4ed8;
  cursor: pointer;
  font-weight: 700;
  min-width: 34px;
  padding: 5px 10px;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.contents-duplication-count:hover { background: #dbeafe; }
.contents-state { flex: 1 1 auto; }
.contents-state, .contents-empty { color: #4b5563; padding: 48px; text-align: center; }
.contents-state--error {
  color: #b91c1c;
  button { color: #2563eb; margin-left: 12px; }
}
.contents-footer { border-top: 1px solid #e5e7eb; color: #4b5563; flex: 0 0 auto; font-size: 12px; padding: 12px 24px; text-align: right; }
@media (max-width: 1100px) {
  .contents-page { height: auto; min-height: calc(100vh - 64px); overflow: visible; }
  .contents-page__content { height: auto; }
  .contents-panel { min-height: 32rem; }
  .contents-summary { grid-template-columns: minmax(0, 1fr); }
  .contents-summary__item + .contents-summary__item::before { bottom: auto; height: 1px; left: 28px; right: 28px; top: 0; width: auto; }
  .contents-toolbar { align-items: stretch; flex-direction: column; }
  .contents-toolbar__actions { flex-wrap: wrap; }
  .contents-search { flex: none; max-width: 100%; width: 100%; }
  .contents-tabs { align-self: flex-start; margin-left: 0; max-width: 100%; overflow-x: auto; }
  .contents-page--internal-generic { height: 100%; min-height: 0; overflow: hidden; }
  .contents-page--internal-generic .contents-page__content { height: calc(100% - 40px); }
}
</style>
